import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, alpha, Box, CircularProgress, Container, IconButton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDocById } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { PROJECTS_COLLECTION } from '../../../constants/collectionPaths';
import UpsertProject from './UpsertProject';

const UpdateProject: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const { projectId } = useParams<{ projectId: string }>();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!db || !projectId) {
        setLoading(false);
        return;
      }
      try {
        await getDocById(null, null, db, PROJECTS_COLLECTION, projectId, false);
      } catch (err) {
        setError(translate('common.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [db, projectId, translate]);

  const handleSave = (_project: any) => {
    setTimeout(() => {
      window.location.href = '/manager/projects/manage';
    }, 2000);
  };

  const handleCancel = () => {
    navigate('/manager/projects/manage');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={handleCancel}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {translate('project.editTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('project.editSubtitle')}
          </Typography>
        </Box>
      </Box>

      <UpsertProject projectId={projectId} onSave={handleSave} onCancel={handleCancel} />
    </Container>
  );
};

export default UpdateProject;
